/**
 * Реализация менеджера Live queries (реактивных запросов)
 */

import { EventEmitter } from 'eventemitter3';
import { CSElement } from '../core/CSElement';
import { generateId } from '../utils/helpers';
import {
  LiveQueryManager,
  LiveQuery,
  LiveQueryConfig,
  LiveQueryStats,
  LiveQuerySubscription,
  LiveQueryEvent,
  LiveQueryBuilder,
  LiveQueryIndex,
  LiveQueryCallback,
  LiveQueryErrorCallback,
  LiveQueryFilter,
  LiveQueryTransform,
  LiveQuerySort,
  QueryOptions
} from '../types/live-query-interfaces';
import { QueryEngine } from '../utils/QueryEngine';
import { ICSElement } from '../types/interfaces';

export class LiveQueryManagerImpl extends EventEmitter implements LiveQueryManager {
  private queries = new Map<string, LiveQuery>();
  private subscriptions = new Map<string, Map<string, LiveQuerySubscription>>();
  private stats: LiveQueryStats;
  private debounceTimers = new Map<string, NodeJS.Timeout>();
  private updateBatches = new Map<string, Set<string>>();
  private indexes = new Map<string, LiveQueryIndex>();
  private CSElementClass: typeof CSElement | null = null;

  constructor() {
    super();
    this.stats = this.createEmptyStats();
  }

  public setCSElementClass(cls: typeof CSElement): void {
    this.CSElementClass = cls;
  }

  createLiveQuery<T = any>(
    selector: string,
    options: QueryOptions = {},
    config: LiveQueryConfig = {}
  ): LiveQuery<T> {
    const id = generateId();
    
    const query: LiveQuery<T> = {
      id,
      selector,
      options,
      config: {
        autoStart: true,
        debounce: 100,
        cache: true,
        cacheTTL: 5000,
        deep: false,
        batch: true,
        ...config
      },
      results: [],
      active: false,
      lastUpdated: 0,
      updateCount: 0,
      watchedElements: new Set(),
      filters: [],
      transforms: [],
      sort: undefined,
      onResults: undefined,
      onError: undefined
    };

    this.queries.set(id, query);
    this.subscriptions.set(id, new Map());
    this.stats.totalQueries++;

    // Обновляем статистику селекторов
    this.updateSelectorStats(selector);

    // Автоматический запуск если включен
    if (query.config.autoStart) {
      this.start(id);
    }

    this.emit('query-created', { queryId: id, query });
    this.emitEvent({
      type: 'query-created',
      queryId: id,
      data: { selector, options, config },
      timestamp: Date.now()
    });

    return query;
  }

  start(queryId: string): void {
    const query = this.queries.get(queryId);
    if (!query || query.active) {
      return;
    }

    query.active = true;
    this.stats.activeQueries++;

    // Выполняем первоначальный запрос
    this.executeQuery(queryId);

    // Настраиваем наблюдение за изменениями
    this.setupQueryWatching(query);

    this.emitEvent({
      type: 'query-started',
      queryId,
      timestamp: Date.now()
    });
  }

  stop(queryId: string): void {
    const query = this.queries.get(queryId);
    if (!query || !query.active) {
      return;
    }

    query.active = false;
    this.stats.activeQueries--;

    // Останавливаем наблюдение
    this.stopQueryWatching(query);

    // Очищаем таймеры debounce
    const timer = this.debounceTimers.get(queryId);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(queryId);
    }

    this.emitEvent({
      type: 'query-stopped',
      queryId,
      timestamp: Date.now()
    });
  }

  getLiveQuery(queryId: string): LiveQuery | undefined {
    return this.queries.get(queryId);
  }

  getAllLiveQueries(): LiveQuery[] {
    return Array.from(this.queries.values());
  }

  removeLiveQuery(queryId: string): boolean {
    const query = this.queries.get(queryId);
    if (!query) {
      return false;
    }

    // Останавливаем запрос
    if (query.active) {
      this.stop(queryId);
    }

    // Удаляем все подписки
    this.subscriptions.delete(queryId);

    // Удаляем запрос
    this.queries.delete(queryId);
    this.stats.totalQueries--;

    this.emitEvent({
      type: 'query-removed',
      queryId,
      timestamp: Date.now()
    });

    return true;
  }

  updateQuery(queryId: string): void {
    const query = this.queries.get(queryId);
    if (!query || !query.active) {
      return;
    }

    if (query.config.debounce && query.config.debounce > 0) {
      // Используем debounce
      const existingTimer = this.debounceTimers.get(queryId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        this.executeQuery(queryId);
        this.debounceTimers.delete(queryId);
      }, query.config.debounce);

      this.debounceTimers.set(queryId, timer);
    } else {
      // Немедленное обновление
      this.executeQuery(queryId);
    }
  }

  updateAllQueries(): void {
    for (const [queryId, query] of this.queries) {
      if (query.active) {
        this.updateQuery(queryId);
      }
    }
  }

  addFilter<T>(queryId: string, filter: LiveQueryFilter<T>): void {
    const query = this.queries.get(queryId);
    if (query) {
      query.filters.push(filter);
      this.updateQuery(queryId);
    }
  }

  removeFilter(queryId: string, filterIndex: number): void {
    const query = this.queries.get(queryId);
    if (query && filterIndex >= 0 && filterIndex < query.filters.length) {
      query.filters.splice(filterIndex, 1);
      this.updateQuery(queryId);
    }
  }

  addTransform<T, R>(queryId: string, transform: LiveQueryTransform<T, R>): void {
    const query = this.queries.get(queryId);
    if (query) {
      query.transforms.push(transform);
      this.updateQuery(queryId);
    }
  }

  setSort<T>(queryId: string, sort: LiveQuerySort<T>): void {
    const query = this.queries.get(queryId);
    if (query) {
      query.sort = sort;
      this.updateQuery(queryId);
    }
  }

  subscribe<T>(queryId: string, callback: LiveQueryCallback<T>): string {
    const subscriptionId = generateId();
    const subscriptions = this.subscriptions.get(queryId);
    
    if (subscriptions) {
      const subscription: LiveQuerySubscription = {
        id: subscriptionId,
        queryId,
        callback,
        active: true,
        createdAt: Date.now()
      };

      subscriptions.set(subscriptionId, subscription);
    }

    return subscriptionId;
  }

  unsubscribe(queryId: string, subscriptionId: string): boolean {
    const subscriptions = this.subscriptions.get(queryId);
    if (subscriptions) {
      return subscriptions.delete(subscriptionId);
    }
    return false;
  }

  getStats(): LiveQueryStats {
    // Обновляем актуальную статистику
    this.updateStats();
    return { ...this.stats };
  }

  clear(): void {
    // Останавливаем все активные запросы
    for (const [queryId, query] of this.queries) {
      if (query.active) {
        this.stop(queryId);
      }
    }

    // Очищаем все данные
    this.queries.clear();
    this.subscriptions.clear();
    this.debounceTimers.clear();
    this.updateBatches.clear();
    this.indexes.clear();

    // Сбрасываем статистику
    this.stats = this.createEmptyStats();
  }

  notifyElementChange(element: CSElement, _changeType: 'create' | 'update' | 'delete'): void {
    // Находим все запросы, которые могут быть затронуты этим изменением
    const affectedQueries = this.findAffectedQueries(element);

    for (const queryId of affectedQueries) {
      this.updateQuery(queryId);
    }
  }

  notifyDataChange(element: CSElement, key: string, _newValue: any, _oldValue: any): void {
    // Находим запросы, которые используют этот ключ данных
    const affectedQueries = this.findQueriesUsingDataKey(element, key);

    for (const queryId of affectedQueries) {
      this.updateQuery(queryId);
    }
  }

  // Приватные методы

  private async executeQuery(queryId: string): Promise<void> {
    const query = this.queries.get(queryId);
    if (!query) {
      return;
    }

    const startTime = Date.now();

    try {
      // Выполняем базовый запрос
      let results = await this.performQuery(query);

      // Применяем фильтры
      for (const filter of query.filters) {
        results = results.filter(filter);
      }

      // Применяем трансформации
      for (const transform of query.transforms) {
        results = results.map(transform);
      }

      // Применяем сортировку
      if (query.sort) {
        results.sort(query.sort);
      }

      // Применяем лимит
      if (query.config.limit && query.config.limit > 0) {
        results = results.slice(0, query.config.limit);
      }

      // Обновляем результаты
      const oldResults = query.results;
      query.results = results;
      query.lastUpdated = Date.now();
      query.updateCount++;

      // Обновляем статистику
      this.stats.totalUpdates++;
      const executionTime = Date.now() - startTime;
      this.updateExecutionTimeStats(executionTime);

      // Уведомляем подписчиков
      this.notifySubscribers(queryId, results, query);

      // Вызываем callback если установлен
      if (query.onResults) {
        query.onResults(results, query);
      }

      this.emitEvent({
        type: 'query-updated',
        queryId,
        data: { 
          results, 
          oldResults, 
          executionTime,
          changeCount: this.calculateChangeCount(oldResults, results)
        },
        timestamp: Date.now()
      });

      this.emitEvent({
        type: 'results-changed',
        queryId,
        data: { results, executionTime },
        timestamp: Date.now()
      });

    } catch (error) {
      // Обработка ошибок
      if (query.onError) {
        query.onError(error as Error, query);
      }

      this.emit('query-error', { queryId, error });
      console.error(`Live query ${queryId} error:`, error);
    }
  }

  private async performQuery(query: LiveQuery): Promise<any[]> {
    if (!this.CSElementClass) {
      throw new Error('CSElementClass has not been set on LiveQueryManager.');
    }
    try {
      const root = query.options?.root;
      if (!root) {
        // Fallback to searching all elements if no root is provided.
        const allElements = this.CSElementClass.getAllElements();
        const results: ICSElement[] = [];
        allElements.forEach((el: ICSElement) => {
          results.push(...QueryEngine.query(el, query.selector));
        });
        return Array.from(new Set(results)); // Remove duplicates
      }
      
      // Если есть корневой элемент, ищем только в его контексте
      return QueryEngine.query(root, query.selector);
    } catch (error) {
      console.warn('Error executing query:', error);
      return [];
    }
  }

  private setupQueryWatching(_query: LiveQuery): void {
    // Эта логика теперь полностью управляется через
    // вызовы notifyElementChange и notifyDataChange,
    // которые инициируют updateQuery.
    // Использование computed здесь было бы некорректным,
    // так как система реактивности не отслеживает
    // добавление/удаление дочерних элементов напрямую.
  }

  private stopQueryWatching(_query: LiveQuery): void {
    // Останавливаем наблюдение за элементами
    // Это будет реализовано при интеграции с ReactivityManager
  }

  private findAffectedQueries(element: CSElement): string[] {
    const affected: string[] = [];

    for (const [queryId, query] of this.queries) {
      if (query.active && this.queryAffectedByElement(query, element)) {
        affected.push(queryId);
      }
    }

    return affected;
  }

  private findQueriesUsingDataKey(_element: CSElement, key: string): string[] {
    const affected: string[] = [];

    for (const [queryId, query] of this.queries) {
      if (query.active && this.queryUsesDataKey(query, key)) {
        affected.push(queryId);
      }
    }

    return affected;
  }

  private queryAffectedByElement(query: LiveQuery, element: CSElement): boolean {
    // Проверяем, может ли изменение элемента повлиять на результаты запроса
    // Это упрощенная логика, в реальности будет более сложная
    return query.watchedElements.has(element.id) || 
           query.selector.includes(element.name) ||
           query.selector.includes('*');
  }

  private queryUsesDataKey(query: LiveQuery, key: string): boolean {
    // Проверяем, использует ли запрос определенный ключ данных
    return query.selector.includes(`[${key}]`) || 
           query.selector.includes(`data-${key}`) ||
           query.selector.includes(`@${key}`);
  }

  private notifySubscribers(queryId: string, results: any[], query: LiveQuery): void {
    const subscriptions = this.subscriptions.get(queryId);
    if (!subscriptions) {
      return;
    }

    for (const subscription of subscriptions.values()) {
      if (subscription.active) {
        try {
          subscription.callback(results, query);
        } catch (error) {
          console.error(`Subscription callback error for query ${queryId}:`, error);
        }
      }
    }
  }

  private calculateChangeCount(oldResults: any[], newResults: any[]): number {
    // Простой подсчет изменений
    if (oldResults.length !== newResults.length) {
      return Math.abs(oldResults.length - newResults.length);
    }

    let changes = 0;
    for (let i = 0; i < oldResults.length; i++) {
      if (oldResults[i] !== newResults[i]) {
        changes++;
      }
    }

    return changes;
  }

  private updateSelectorStats(selector: string): void {
    let selectorStats = this.stats.selectorStats.get(selector);
    
    if (!selectorStats) {
      selectorStats = {
        selector,
        queryCount: 0,
        averageResults: 0,
        lastUsed: Date.now(),
        totalExecutionTime: 0
      };
      this.stats.selectorStats.set(selector, selectorStats);
    }

    selectorStats.queryCount++;
    selectorStats.lastUsed = Date.now();
  }

  private updateExecutionTimeStats(executionTime: number): void {
    const currentAverage = this.stats.averageQueryTime;
    const totalQueries = this.stats.totalUpdates;
    
    this.stats.averageQueryTime = 
      (currentAverage * (totalQueries - 1) + executionTime) / totalQueries;
  }

  private updateStats(): void {
    this.stats.watchedElements = 0;
    this.stats.memoryUsage = this.calculateMemoryUsage();

    for (const query of this.queries.values()) {
      this.stats.watchedElements += query.watchedElements.size;
    }
  }

  private calculateMemoryUsage(): number {
    // Упрощенный расчет использования памяти
    let usage = 0;
    
    for (const query of this.queries.values()) {
      usage += JSON.stringify(query.results).length;
      usage += query.selector.length;
      usage += query.watchedElements.size * 50; // Примерный размер ID элемента
    }

    return usage;
  }

  private createEmptyStats(): LiveQueryStats {
    return {
      totalQueries: 0,
      activeQueries: 0,
      totalUpdates: 0,
      averageQueryTime: 0,
      memoryUsage: 0,
      watchedElements: 0,
      selectorStats: new Map()
    };
  }

  private emitEvent(event: LiveQueryEvent): void {
    this.emit('event', event);
    this.emit(event.type, event);
  }
}



// Builder для удобного создания Live queries
export class LiveQueryBuilderImpl<T = any> implements LiveQueryBuilder<T> {
  private _selector = '';
  private _options: QueryOptions = {};
  private _config: LiveQueryConfig = {};
  private _filters: LiveQueryFilter<T>[] = [];
  private _transforms: LiveQueryTransform<T, any>[] = [];
  private _sort?: LiveQuerySort<T>;
  private _onResults?: LiveQueryCallback<T>;
  private _onError?: LiveQueryErrorCallback;
  private manager: LiveQueryManager;

  constructor(manager: LiveQueryManager) {
    this.manager = manager;
  }

  selector(selector: string): LiveQueryBuilder<T> {
    this._selector = selector;
    return this;
  }

  options(options: QueryOptions): LiveQueryBuilder<T> {
    this._options = { ...this._options, ...options };
    return this;
  }

  config(config: LiveQueryConfig): LiveQueryBuilder<T> {
    this._config = { ...this._config, ...config };
    return this;
  }

  filter(filter: LiveQueryFilter<T>): LiveQueryBuilder<T> {
    this._filters.push(filter);
    return this;
  }

  transform<R>(transform: LiveQueryTransform<T, R>): LiveQueryBuilder<R> {
    this._transforms.push(transform);
    return this as any;
  }

  sort(sort: LiveQuerySort<T>): LiveQueryBuilder<T> {
    this._sort = sort;
    return this;
  }

  limit(limit: number): LiveQueryBuilder<T> {
    this._config.limit = limit;
    return this;
  }

  debounce(ms: number): LiveQueryBuilder<T> {
    this._config.debounce = ms;
    return this;
  }

  cache(ttl?: number): LiveQueryBuilder<T> {
    this._config.cache = true;
    if (ttl !== undefined) {
      this._config.cacheTTL = ttl;
    }
    return this;
  }

  subscribe(callback: LiveQueryCallback<T>): LiveQueryBuilder<T> {
    this._onResults = callback;
    return this;
  }

  onError(callback: LiveQueryErrorCallback): LiveQueryBuilder<T> {
    this._onError = callback;
    return this;
  }

  start(): LiveQuery<T> {
    const query = this.build();
    this.manager.start(query.id);
    return query;
  }

  build(): LiveQuery<T> {
    const query = this.manager.createLiveQuery<T>(
      this._selector,
      this._options,
      { ...this._config, autoStart: false }
    );

    // Применяем фильтры, трансформации и сортировку
    for (const filter of this._filters) {
      this.manager.addFilter(query.id, filter);
    }

    for (const transform of this._transforms) {
      this.manager.addTransform(query.id, transform);
    }

    if (this._sort) {
      this.manager.setSort(query.id, this._sort);
    }

    // Устанавливаем callbacks
    if (this._onResults) {
      query.onResults = this._onResults;
    }

    if (this._onError) {
      query.onError = this._onError;
    }

    return query;
  }
} 